import { CancelledError } from "@tanstack/react-query";
import { AxiosError, isAxiosError } from "axios";
import { toast } from "react-toastify";

export const handleDisplayErrorData = (
  message: string,
  title?: string,
  error?: Error,
  onCloseHandler?: () => void,
  autoClose: number | false = false
) => {
  if (isAxiosError(error) && error.response?.status === 403) {
    return;
  }
  if (error instanceof CancelledError) {
    return;
  }
  const errorMessages = getErrorMessages(error);
  const displayMessage = title ? `${title}: ${message}` : message;
  const errorDetails = errorMessages.length > 0 && errorMessages[0] ? ` - ${errorMessages[0].message}` : "";
  toast.error(`${displayMessage}${errorDetails}`, {
    autoClose: autoClose,
    style: { width: message.length > 60 ? "800px" : "600px" },
    onClose: onCloseHandler,
  });
};

export type HandleDisplayErrorDataCallBack = typeof handleDisplayErrorData;

function getErrorMessages(error?: Error | string): Error[] {
  if (!error) {
    return [];
  }
  let errorMessage: string;
  if (typeof error === "string") {
    errorMessage = parseStringError(error);
  } else if (isAxiosError(error) || (error && typeof error === "object" && "response" in error && "config" in error)) {
    errorMessage = extractAxiosErrorMessage(error as AxiosError);
  } else if (error instanceof Error) {
    errorMessage = error.message || "Unknown error";
  } else {
    errorMessage = formatGenericError(error);
  }
  return [new Error(errorMessage)];
}

function extractAxiosErrorMessage(error: AxiosError): string {
  const { data } = error.response ?? {};

  if (typeof data === "object" && data !== null) {
    return (data as { message?: string })?.message ?? JSON.stringify(data);
  }

  return typeof data === "string" ? data : error.message || "Unknown error";
}

function parseStringError(error: string): string {
  if (error.includes("<!DOCTYPE html>") || error.includes("<html>")) {
    return "Server returned an HTML response instead of expected data";
  }
  if (!isJsonString(error)) {
    return error;
  }
  try {
    const parsedError = JSON.parse(error);
    if (!parsedError) {
      return error;
    }

    return extractMessageFromObject(parsedError);
  } catch {
    return error;
  }
}

function isJsonString(str: string): boolean {
  const trimmed = str.trim();
  return trimmed.startsWith("{") || trimmed.startsWith("[");
}

function formatGenericError(error: unknown): string {
  try {
    if (typeof error === "object" && error !== null) {
      return formatErrorObject(error as Record<string, unknown>);
    }
    return String(error);
  } catch {
    return "Unknown error";
  }
}

function extractMessageFromObject(obj: Record<string, unknown>): string {
  if (typeof obj !== "object" || obj === null) {
    return String(obj);
  }
  if (obj.data) {
    if (typeof obj.data === "string") {
      return obj.data;
    }
    if (typeof obj.data === "object" && obj.data !== null && "message" in obj.data) {
      return String(obj.data.message);
    }
  }
  if (obj.message) {
    return typeof obj.message === "string" ? obj.message : JSON.stringify(obj.message);
  }
  if (obj.error) {
    return typeof obj.error === "string" ? obj.error : formatErrorObject(obj.error as Record<string, unknown>);
  }
  return formatErrorObject(obj);
}

function formatErrorObject(obj: Record<string, unknown>): string {
  try {
    if ("message" in obj && obj.message) {
      return typeof obj.message === "string" ? obj.message : JSON.stringify(obj.message);
    }

    if ("error" in obj && obj.error) {
      return typeof obj.error === "string" ? obj.error : formatErrorObject(obj.error as Record<string, unknown>);
    }

    const result = JSON.stringify(obj, null, 2);
    return result === "{}" ? "Unknown error details" : result;
  } catch {
    return "Error details could not be displayed";
  }
}
